/**
 * Frequency Calculations for PlanLens Budgeting
 * Handles normalization of amounts across different payment frequencies
 */

/**
 * Normalize any amount to monthly equivalent
 * @param {number} amount - Original amount
 * @param {string} frequency - weekly|monthly|quarterly|yearly|one-off
 * @returns {number} Monthly equivalent amount
 */
export const normalizeToMonthly = (amount, frequency) => {
  if (!amount || isNaN(amount)) return 0;

  const multipliers = {
    weekly: 4.333,      // 52 weeks / 12 months
    monthly: 1,
    quarterly: 1/3,     // 4 quarters / 12 months
    yearly: 1/12,
    'one-off': 0        // Exclude from recurring baseline
  };

  return amount * (multipliers[frequency] || 1);
};

/**
 * Convert monthly amount back to original frequency
 * @param {number} monthlyAmount - Monthly equivalent
 * @param {string} targetFrequency - Desired frequency
 * @returns {number} Amount in target frequency
 */
export const denormalizeFromMonthly = (monthlyAmount, targetFrequency) => {
  if (!monthlyAmount || isNaN(monthlyAmount)) return 0;

  const divisors = {
    weekly: 4.333,
    monthly: 1,
    quarterly: 1/3,
    yearly: 1/12,
    'one-off': 1
  };

  return monthlyAmount / (divisors[targetFrequency] || 1);
};

/**
 * Calculate annual equivalent (for retirement projections)
 * @param {number} amount - Original amount
 * @param {string} frequency - Payment frequency
 * @returns {number} Annual equivalent
 */
export const normalizeToYearly = (amount, frequency) => {
  return normalizeToMonthly(amount, frequency) * 12;
};

/**
 * Get human-readable frequency label
 * @param {string} frequency - Frequency code
 * @returns {string} Display label
 */
export const getFrequencyLabel = (frequency) => {
  const labels = {
    weekly: 'per week',
    monthly: 'per month',
    quarterly: 'per quarter',
    yearly: 'per year',
    'one-off': 'one-off'
  };
  return labels[frequency] || '';
};

/**
 * Get short frequency label for display
 * @param {string} frequency - Frequency code
 * @returns {string} Short label
 */
export const getFrequencyShortLabel = (frequency) => {
  const labels = {
    weekly: '/wk',
    monthly: '/mo',
    quarterly: '/qtr',
    yearly: '/yr',
    'one-off': 'once'
  };
  return labels[frequency] || '';
};

/**
 * Format amount with frequency display
 * Shows conversion if frequency is not monthly
 * @param {number} amount - Original amount
 * @param {string} frequency - Payment frequency
 * @param {string} currency - USD or GBP
 * @param {boolean} showConversion - Show monthly equivalent
 * @returns {string} Formatted display string
 */
export const formatBudgetAmount = (amount, frequency, currency = 'GBP', showConversion = true) => {
  const symbol = currency === 'USD' ? '$' : '£';

  const formatted = `${symbol}${amount.toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}${getFrequencyShortLabel(frequency)}`;

  if (showConversion && frequency !== 'monthly' && frequency !== 'one-off') {
    const monthlyEq = normalizeToMonthly(amount, frequency);
    const monthlyFormatted = `${symbol}${monthlyEq.toLocaleString('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })}/mo`;
    return `${formatted} → ${monthlyFormatted}`;
  }

  return formatted;
};

/**
 * Get all available frequency options
 * @returns {Array} Array of frequency options
 */
export const getFrequencyOptions = () => {
  return [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'one-off', label: 'One-off' }
  ];
};

/**
 * Calculate number of payments per year
 * @param {string} frequency - Payment frequency
 * @returns {number} Payments per year
 */
export const getPaymentsPerYear = (frequency) => {
  const paymentsPerYear = {
    weekly: 52,
    monthly: 12,
    quarterly: 4,
    yearly: 1,
    'one-off': 0
  };
  return paymentsPerYear[frequency] || 12;
};

/**
 * Determine if a cost is fixed or variable
 * Fixed costs: Same amount each period (contractual/recurring bills)
 * Variable costs: Fluctuating amounts (discretionary spending)
 * @param {string} categoryName - Category name
 * @returns {string} 'fixed' or 'variable'
 */
export const getCostType = (categoryName) => {
  // Fixed costs - regular bills and contractual obligations
  const fixedCategories = [
    // Housing
    'mortgage', 'rent', 'council tax', 'ground rent', 'service charge',
    'buildings insurance', 'contents insurance', 'home insurance',

    // Utilities (mostly fixed)
    'electricity', 'gas', 'water', 'internet', 'broadband',
    'mobile phone', 'phone', 'landline', 'tv license',

    // Transport
    'car finance', 'car insurance', 'car tax', 'mot', 'road tax',
    'season ticket', 'travel pass', 'parking permit',

    // Insurance & Financial
    'life insurance', 'health insurance', 'dental insurance',
    'pet insurance', 'travel insurance', 'income protection',
    'loan repayment', 'credit card repayment', 'debt repayment',
    'pension contribution', 'regular savings', 'standing order',

    // Subscriptions & Memberships
    'subscription', 'netflix', 'spotify', 'disney', 'amazon prime',
    'gym membership', 'club membership', 'magazine subscription',

    // Family & Education
    'school fees', 'university fees', 'tuition', 'childcare',
    'nursery', 'after school club', 'child maintenance',

    // Other regular commitments
    'charity donation', 'union fees', 'professional membership'
  ];

  const categoryLower = categoryName.toLowerCase();
  const isFixed = fixedCategories.some(fixed =>
    categoryLower.includes(fixed)
  );

  return isFixed ? 'fixed' : 'variable';
};

/**
 * Calculate average monthly amount from transaction history
 * @param {Array} transactions - Array of transactions
 * @param {number} months - Number of months to average
 * @returns {number} Average monthly amount
 */
export const calculateAverageMonthly = (transactions, months = 3) => {
  if (!transactions || transactions.length === 0) return 0;

  const total = transactions.reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
  return total / months;
};

/**
 * Validate frequency value
 * @param {string} frequency - Frequency to validate
 * @returns {boolean} Is valid
 */
export const isValidFrequency = (frequency) => {
  const validFrequencies = ['weekly', 'monthly', 'quarterly', 'yearly', 'one-off'];
  return validFrequencies.includes(frequency);
};
